import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid, Grow, createStyles, makeStyles } from '@material-ui/core'
import TextField from '../TextField/TextField'
import GoogleMapsApi from '../Global/GoogleMapsApi'
import CMSText, { prismicText } from '../Global/CMSText'
import {
  GoogleMapAddress,
  PayloadAddress,
} from '@/redux/reducers/addressReducer'
import { useAddress } from '@/hooks/useAddress'
import clsx from 'clsx'
export const STREET_NUMBER_AFTER_STREET = ['DE']

interface GoogleMapAddressFormProps {
  account?: boolean
  prefill?: boolean
  defaultAddress?: any
  addressType: 'billing' | 'shipping'
  hideAdditionalAddress?: boolean
  className?: string
}

const useStyles = makeStyles((theme) =>
  createStyles({
    marginBottom: {
      marginBottom: theme.spacing(2),
    },
  })
)

const GoogleMapAddressForm: React.FC<GoogleMapAddressFormProps> = ({
  account = false,
  prefill = false,
  defaultAddress = null,
  addressType,
  hideAdditionalAddress = false,
  className,
}) => {
  const { t } = useTranslation()
  const postCodeRequired =
    process.env.POSTCODE_REQUIRED?.toLowerCase() !== 'false'
  const classes = useStyles()
  const prismicGeneral = useSelector((state) => state.prismic.general)
  const prismicData = useSelector(
    (state) => state.prismic[account ? 'account' : 'funnel']
  )

  function formatPayloadStreet(
    countryCode: string,
    streetName: string,
    streetNumber?: number
  ) {
    if (STREET_NUMBER_AFTER_STREET.includes(countryCode)) {
      return `${streetName}${streetNumber ? ` ${streetNumber}` : ''}`.trim()
    }

    return `${streetNumber ? `${streetNumber} ` : ''}${streetName}`.trim()
  }

  function formatPayloadAddress(
    inputAddress: GoogleMapAddress,
    countries: any
  ) {
    const {
      street,
      streetNumber,
      postcode,
      city,
      country,
      details,
      areaCode,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      area,
    } = inputAddress
    const isValid = postCodeRequired
      ? Boolean(street && postcode && city && country && streetNumber)
      : Boolean(street && city && country && streetNumber)

    let payloadAddress
    if (isValid) {
      const countryCode = countries
        .filter((country) => country.name === inputAddress.country)[0]
        .code.toUpperCase()

      payloadAddress = {
        street: formatPayloadStreet(countryCode, street, streetNumber),
        details: details?.trim(),
        postcode: postcode?.trim(),
        city: city.trim(),
        countryCode,
      }

      if (process.env.SEND_PROVINCE_CODE?.toLowerCase() === 'true') {
        payloadAddress.provinceCode = areaCode?.trim()
        // We can't send both provinceName and provinceCode for now on sylius.
        // provinceName: area.trim(),
      }
    }

    return { isValid, payloadAddress }
  }

  function getStreetAndStreetNumber(streetString: string, countryCode: string) {
    const splitStreetAndNumber = streetString?.split(' ')
    let street = streetString
    let streetNumber

    if (splitStreetAndNumber?.length > 1) {
      if (STREET_NUMBER_AFTER_STREET.includes(countryCode)) {
        streetNumber = splitStreetAndNumber[splitStreetAndNumber.length - 1]
        street = streetString?.split(String(streetNumber))[0]?.trim()
      } else {
        streetNumber = splitStreetAndNumber[0]
        street = streetString?.split(String(streetNumber))[1]?.trim()
      }
    }

    return { street, streetNumber }
  }

  function getStreet(streetString: string) {
    const splitStreetAndDetails = streetString?.split(' - ')
    return splitStreetAndDetails?.length
      ? splitStreetAndDetails[0]
      : streetString
  }

  function initInputAddress(shippingAddress: PayloadAddress, countries: any) {
    let inputAddress
    if (prefill && shippingAddress) {
      const {
        city,
        details,
        postcode,
        countryCode,
        provinceName,
        provinceCode,
      } = shippingAddress
      const streetWithNumber = getStreet(shippingAddress.street)
      const { street, streetNumber } = getStreetAndStreetNumber(
        streetWithNumber,
        countryCode
      )
      const country = countries.filter(
        (country) => country.code === shippingAddress.countryCode
      )?.[0]?.name

      inputAddress = {
        street,
        details,
        streetNumber,
        city,
        postcode,
        area: provinceName,
        areaCode: provinceCode,
        country,
      }
    } else if (
      prefill &&
      defaultAddress &&
      defaultAddress.street.length !== 0
    ) {
      const { city, details, postcode, countryCode } = defaultAddress
      const streetWithNumber = getStreet(defaultAddress.street)
      const { street, streetNumber } = getStreetAndStreetNumber(
        streetWithNumber,
        countryCode
      )
      const country = countries.filter(
        (country) => country.code === defaultAddress.countryCode
      )[0].name

      inputAddress = {
        street,
        details,
        streetNumber,
        city,
        postcode,
        country,
      }
    } else {
      inputAddress = {
        street: '',
        details: '',
        streetNumber: undefined,
        city: '',
        postcode: '',
        country: '',
      }
    }
    return inputAddress
  }

  const { saveSearchAddress, address, saveInputAddress } = useAddress(
    addressType,
    initInputAddress,
    formatPayloadAddress
  )
  useEffect(() => {
    if (address.search) {
      saveInputAddress({
        street: address.search.street,
        city: address.search.city,
        country: address.search.country,
        postcode: address.search.postcode,
        area: address.search.area,
        areaCode: address.search.areaCode,
        details: '',
        streetNumber: address.search.streetNumber,
      })
    }
    if (address.search && !postCodeRequired) {
      saveInputAddress({
        ...address.search,
        // add "00000" when postcode is not required (but it is on Sylius)
        postcode: address.search.postcode || '00000',
      })
    }
  }, [address.search])

  const inputAddress = address.input as GoogleMapAddress

  function onChange(value, key) {
    saveInputAddress({
      ...inputAddress,
      [key]: value,
    })
  }

  const text = (key: string) =>
    prismicText(prismicData?.[key], t(`texts:account:${key}`))

  const placeholder = prismicText(
    prismicGeneral?.placeholder,
    t('texts:general:search_address:placeholder')
  )

  const addressComplete = [
    address?.payload?.street,
    address?.payload?.city,
    address?.payload?.postcode === '00000' ? '' : address?.payload?.postcode,
    address?.payload?.countryCode,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <Grid
        container
        spacing={1}
        className={clsx(classes.marginBottom, className)}
      >
        <Grid item xs={12}>
          <GoogleMapsApi
            datas={prismicGeneral}
            label={
              <CMSText
                asText
                data={
                  addressType === 'shipping' ? prismicGeneral?.label : undefined
                }
                defaultText={
                  addressType === 'billing'
                    ? t('texts:general:search_address:label_billing')
                    : t('texts:general:search_address:label')
                }
              />
            }
            placeholder={placeholder}
            saveSearchAddress={saveSearchAddress}
            value={defaultAddress ? addressComplete : null}
          />
        </Grid>
      </Grid>
      {address.isPanelOpen && (
        <Grow in mountOnEnter>
          <>
            <Grid container spacing={1} className={classes.marginBottom}>
              <Grid item xs={3} sm={2}>
                <TextField
                  label={text('street_number')}
                  type="text"
                  name="streetNumber"
                  value={inputAddress.streetNumber}
                  onChange={(value) => onChange(value, 'streetNumber')}
                />
              </Grid>
              <Grid item xs={9} sm={10}>
                <TextField
                  label={text('address')}
                  placeholder={text('address')}
                  type="text"
                  name="address"
                  value={inputAddress.street}
                  onChange={(value) => onChange(value, 'street')}
                />
              </Grid>
            </Grid>
            {!hideAdditionalAddress && (
              <Grid container spacing={1} className={classes.marginBottom}>
                <Grid item xs={8}>
                  <TextField
                    label={text('address_details')}
                    placeholder={text('address_details')}
                    type="text"
                    name="address-details"
                    value={inputAddress.details}
                    onChange={(value) => onChange(value, 'details')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={text('address_area')}
                    placeholder={text('address_area')}
                    type="text"
                    name="address_area"
                    value={inputAddress.area}
                    onChange={(value) => onChange(value, 'area')}
                  />
                </Grid>
              </Grid>
            )}
            <Grid container spacing={1} className={classes.marginBottom}>
              <Grid item xs={6} sm={3}>
                <TextField
                  label={text('postal_code')}
                  placeholder={text('postal_code')}
                  type="text"
                  name="postcode"
                  value={
                    inputAddress.postcode === '00000'
                      ? ''
                      : inputAddress.postcode
                  }
                  onChange={(value) => onChange(value, 'postcode')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label={text('country')}
                  placeholder={text('country')}
                  type="text"
                  name="country"
                  value={inputAddress.country}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={text('town')}
                  placeholder={text('town')}
                  type="text"
                  name="city"
                  value={inputAddress.city}
                  onChange={(value) => onChange(value, 'city')}
                />
              </Grid>
            </Grid>
          </>
        </Grow>
      )}
    </>
  )
}

export default GoogleMapAddressForm
